import type { ReactNode } from "react";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { GlassView, isGlassEffectAPIAvailable } from "expo-glass-effect";
import { GlassButton } from "./GlassButton";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors } from "../theme/tokens";

export type GlassAuthButtonProps = {
  testID?: string;
  label: string;
  icon: ReactNode;
  onPress: () => void;
  disabled?: boolean;
  accessibilityLabel: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
};

/**
 * Shared provider button. Web and Android use the standard GlassButton;
 * supported iOS versions use the native Glass surface with the same content.
 */
export function GlassAuthButton({
  testID,
  label,
  icon,
  onPress,
  disabled,
  action,
  accessibilityLabel,
}: GlassAuthButtonProps) {
  // Captures only on the native-Pressable branch below; the GlassButton
  // branch captures its own presses.
  const capturePress = usePressCapture("GlassAuthButton");
  const hasIOSGlassEffect =
    Platform.OS === "ios" && isGlassEffectAPIAvailable();

  if (Platform.OS !== "ios") {
    return (
      <GlassButton
        variant="frosted"
        size="large"
        testID={testID}
        label={label}
        icon={icon}
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
        action={action}
        style={{ flex: 1 }}
      />
    );
  }

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      style={[authButtonStyles.button, { opacity: disabled ? 0.55 : 1 }]}
    >
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFill,
          { backgroundColor: "rgba(255, 244, 236, 0.055)" },
        ]}
      />
      {hasIOSGlassEffect ? (
        <GlassView
          pointerEvents="none"
          glassEffectStyle="regular"
          tintColor="rgba(71, 28, 10, 0.18)"
          colorScheme="dark"
          isInteractive
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      <View style={authButtonStyles.content}>
        {icon}
        <View style={authButtonStyles.labelContainer}>
          <Text style={authButtonStyles.label} numberOfLines={1}>
            {label}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

const authButtonStyles = StyleSheet.create({
  button: {
    position: "relative",
    flex: 1,
    height: 54,
    overflow: "hidden",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.28)",
    borderTopColor: "rgba(255, 255, 255, 0.38)",
    borderBottomColor: "rgba(255, 255, 255, 0.12)",
  },
  content: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 3,
    elevation: 3,
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    paddingHorizontal: 16,
  },
  label: {
    zIndex: 2,
    color: semanticColors.onDark.text.primary,
    fontSize: 14,
    fontWeight: "500",
  },
  labelContainer: {
    zIndex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
});
